/**
 * The v2 editing lane's adapter (slate-feature-transfer W6 §D3).
 *
 * Four envelope conventions in one wave, so each namespace states which it rides:
 *   `editorV2`  — `api/v2/documents/*`, **bare** objects
 *   `selection` — `api/ai-actions/*`, **bare**, plus one `text/event-stream`
 *   `mentions`  — `suggestions.mention`, `{pagination, data:{documents, users}}`
 *   `quotes`    — `pulse/quotes.get`, **bare**, and ALWAYS HTTP 200
 *
 * Everything rides the host's bearer-injected transport. A raw `fetch` from here would arrive
 * anonymous under PKCE — the standing slate trap (the World-page outage of 2026-08-18).
 */

import {
  AiActionDecisionSchema,
  type AiActionRequest,
  type DeriveOutcome,
  DeriveOutcomeSchema,
  DISPATCH_TERMINAL,
  type EditorV2Commit,
  EditorV2CommitSchema,
  type EditorV2Lane,
  EditorV2LaneSchema,
  type EditorV2OptIn,
  EditorV2OptInSchema,
  type IdeDispatch,
  IdeDispatchSchema,
  type MentionSuggestions,
  MentionSuggestionsSchema,
  QUOTE_SYMBOL_LIMIT,
  type QuoteSnapshot,
  QuoteSnapshotSchema,
  type RefineEvent,
  type RefineRequest,
  TransformDispatchSchema,
  type TransformOutcome,
  type TransformRequest,
  TransformTextSchema,
} from "~/api/editorTypes";
import { ForbiddenError } from "~/api/errors";

/**
 * The three lanes this wave genuinely needs, injected by the host adapter.
 *
 * `restStatus` exists because ONE route's meaning lives in its status code:
 * `transform-selection` answers 200 with text or **202 with a dispatch id**, and a plain
 * body-only transport cannot tell the two apart when the body happens to carry both shapes.
 */
export interface EditorTransport {
  /** Bare-JSON REST/POST lane: bearer, 401-refresh, typed 403/404, tolerant of empty bodies. */
  rest(path: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
  /** Same lane, but the caller also needs the HTTP status. */
  restStatus(
    path: string,
    method: string,
    body?: Record<string, unknown>,
  ): Promise<{ status: number; body: unknown }>;
  /** Outline-era dot-RPC (`{data[, policies]}` or a bespoke envelope). Returns the whole payload. */
  rpc(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** `text/event-stream` lane. Resolves when the stream terminates; frames arrive via `onFrame`. */
  stream(
    path: string,
    body: Record<string, unknown>,
    onFrame: (payload: unknown) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  /** `auth.info`'s untyped `data` — where the collaboration token rides. */
  authInfoRaw(): Promise<Record<string, unknown>>;
}

const obj = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

const encode = (value: string): string => encodeURIComponent(value);

export function createKnowItEditor(transport: EditorTransport) {
  const v2 = (documentId: string, suffix = ""): string =>
    `v2/documents/${encode(documentId)}${suffix}`;

  const editorV2 = {
    get: async (documentId: string): Promise<EditorV2Lane> =>
      EditorV2LaneSchema.parse(obj(await transport.rest(v2(documentId), "GET"))),

    optIn: async (documentId: string): Promise<EditorV2OptIn> =>
      EditorV2OptInSchema.parse(obj(await transport.rest(v2(documentId, "/opt-in"), "POST", {}))),

    rollback: async (documentId: string): Promise<{ lane: "v1" }> => {
      await transport.rest(v2(documentId, "/rollback"), "POST", {});
      // The server answers `{lane:"v1"}`; the caller re-reads the document either way, so the
      // lane is asserted rather than parsed — a 409 `not_on_v2_lane` has already thrown.
      return { lane: "v1" };
    },

    /**
     * The dual-ACL gate. **403 is not an error here.** The server refuses an `act` intent it
     * will not grant rather than quietly downgrading it, and the caller's correct next move is
     * to re-ask with `requestedLane:"propose"`. Throwing would turn a designed negotiation
     * into a failure toast; `null` is the answer the editor is built to receive.
     */
    aiAction: async (documentId: string, request: AiActionRequest) => {
      try {
        const body = await transport.rest(v2(documentId, "/ai-action"), "POST", {
          capability: request.capability,
          from: request.from,
          to: request.to,
          ...(request.requestedLane === undefined ? {} : { requestedLane: request.requestedLane }),
          ...(request.thinkerId === undefined ? {} : { thinkerId: request.thinkerId }),
          ...(request.thinkerDisplayName === undefined
            ? {}
            : { thinkerDisplayName: request.thinkerDisplayName }),
          ...(request.model === undefined ? {} : { model: request.model }),
        });
        return AiActionDecisionSchema.parse(obj(body));
      } catch (error) {
        if (error instanceof ForbiddenError) {
          return null;
        }
        throw error;
      }
    },

    commit: async (
      documentId: string,
      range: { from: number; to: number },
    ): Promise<EditorV2Commit> =>
      EditorV2CommitSchema.parse(
        obj(await transport.rest(v2(documentId, "/commit"), "POST", { ...range })),
      ),

    derive: async (
      documentId: string,
      request: { text: string; from: number; to: number },
    ): Promise<DeriveOutcome> =>
      DeriveOutcomeSchema.parse(
        obj(await transport.rest(v2(documentId, "/derive"), "POST", request)),
      ),

    /**
     * The collaboration token — NOT slate's PKCE bearer.
     *
     * It is an HS256 JWT the platform mints for the Hocuspocus spine, with a **24-hour** TTL,
     * and the collab server verifies exactly that signature: an RS256 access token is rejected.
     * Null when the server does not mint one, which the host must render as "collaboration
     * unavailable" rather than connecting with the wrong credential.
     */
    collaborationToken: async (): Promise<string | null> => {
      const data = await transport.authInfoRaw();
      const token = data.collaborationToken;
      return typeof token === "string" && token.length > 0 ? token : null;
    },
  };

  const selection = {
    /**
     * 200 → the text; **202 → a dispatch id** to poll, because the resolved thinker is pinned
     * to somebody's VS Code window. Both are success.
     */
    transform: async (request: TransformRequest): Promise<TransformOutcome> => {
      const { status, body } = await transport.restStatus(
        "ai-actions/transform-selection",
        "POST",
        {
          instructions: request.instructions,
          selectedText: request.selectedText,
          ...(request.documentId === undefined ? {} : { documentId: request.documentId }),
          ...(request.collectionId === undefined ? {} : { collectionId: request.collectionId }),
          ...(request.documentTitle === undefined ? {} : { documentTitle: request.documentTitle }),
          ...(request.thinkerId === undefined ? {} : { thinkerId: request.thinkerId }),
          ...(request.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: request.idempotencyKey }),
        },
      );
      if (status === 202) {
        const dispatch = TransformDispatchSchema.parse(obj(body));
        return { kind: "dispatch", ...dispatch };
      }
      const text = TransformTextSchema.parse(obj(body));
      return {
        kind: "text",
        text: text.transformedText,
        thinkerId: text.thinkerId,
        thinkerName: text.thinkerName,
        model: text.model,
        source: text.source,
        tokensUsed: text.tokensUsed,
      };
    },

    dispatchResult: async (dispatchId: string): Promise<IdeDispatch> =>
      IdeDispatchSchema.parse(
        obj(await transport.rest("ai-actions/ide-dispatch.result", "POST", { dispatchId })),
      ),

    /** True once a dispatch has settled — the poller's stop condition. */
    dispatchSettled: (dispatch: IdeDispatch): boolean =>
      DISPATCH_TERMINAL.has(dispatch.statusDetail.toLowerCase()),

    /**
     * Refine is a **server-sent event stream**, not a request/response. Frames are
     * `data: {json}` and the stream ends on `data: [DONE]`; a torn frame is dropped rather
     * than aborting. The `signal` is what makes an abandoned refinement actually stop — the
     * live kms path has no abort at all (§F6.10).
     */
    refine: async (
      request: RefineRequest,
      onEvent: (event: RefineEvent) => void,
      signal?: AbortSignal,
    ): Promise<void> => {
      await transport.stream(
        "ai-actions/refine-selection",
        {
          originalText: request.originalText,
          currentResult: request.currentResult,
          followUpMessage: request.followUpMessage,
          ...(request.conversationHistory === undefined
            ? {}
            : { conversationHistory: request.conversationHistory }),
          ...(request.documentId === undefined ? {} : { documentId: request.documentId }),
          ...(request.collectionId === undefined ? {} : { collectionId: request.collectionId }),
          ...(request.thinkerId === undefined ? {} : { thinkerId: request.thinkerId }),
        },
        (payload) => {
          const frame = obj(payload);
          const type = frame.type;
          const text = typeof frame.text === "string" ? frame.text : "";
          if (type === "thought" || type === "delta" || type === "result") {
            onEvent({ type, text });
            return;
          }
          if (type === "error") {
            const message = typeof frame.message === "string" ? frame.message : text;
            onEvent({ type: "error", message: message || "The refinement failed." });
          }
        },
        signal,
      );
    },
  };

  const mentions = {
    /**
     * A third envelope shape: `{pagination, data:{documents, users}}`. Access-scoped
     * server-side, so what comes back is already what this viewer may see.
     */
    search: async (
      query: string,
      options: { offset?: number; limit?: number } = {},
    ): Promise<MentionSuggestions> => {
      const payload = obj(
        await transport.rpc("suggestions.mention", {
          ...(query ? { query } : {}),
          offset: options.offset ?? 0,
          limit: options.limit ?? 25,
        }),
      );
      return MentionSuggestionsSchema.parse(obj(payload.data));
    },
  };

  const quotes = {
    /**
     * ALWAYS 200. `{available:false, quotes:[]}` means "no provider is configured" — data, not
     * a failure — so nothing here throws on it. Symbols are normalised, deduped and capped
     * client-side too, because the server silently truncates past its own limit.
     */
    get: async (symbols: string[]): Promise<QuoteSnapshot> => {
      const wanted = [
        ...new Set(symbols.map((s) => s.trim().toUpperCase()).filter((s) => s.length > 0)),
      ].slice(0, QUOTE_SYMBOL_LIMIT);
      if (wanted.length === 0) {
        return { available: false, quotes: [] };
      }
      return QuoteSnapshotSchema.parse(
        obj(await transport.rest("pulse/quotes.get", "POST", { symbols: wanted })),
      );
    },
  };

  return { editorV2, selection, mentions, quotes };
}
